const post = {
  slug: 'how-to-add-a-nominee-in-india',
  title: 'How to add a nominee for bank, PPF, EPF, and insurance',
  excerpt:
    'A practical walkthrough of where nomination actually lives—savings and FDs, PPF, EPF, insurance, and investments—and what to check so the name on the form matches the life you have now.',
  date: '2026-08-27',
  updated: '2026-08-27',
  category: 'How-to',
  featured: false,
  coverImage: '/blog/how-to-add-a-nominee-in-india/cover.jpg',
  coverAlt: 'A calm evening at the desk: folders, a laptop, and the nomination work for every account you actually own.',
  keywords:
    'how to add nominee bank account, PPF nomination, EPF Form 2, LIC nominee, mutual fund nomination, demat nominee India',
  seoTitle: 'How to Add a Nominee for Bank, PPF, EPF & Insurance',
  seoDescription:
    'Step-by-step nomination for Indian bank accounts, PPF, EPF, insurance, and investments. What to fill, what to keep, and what to update after marriage or a new child.',
  related: ['what-is-a-nominee', 'why-nominees-still-need-to-be-told'],
  faqs: [
    {
      q: 'Can I add a nominee online?',
      a: 'Often yes: net banking, UAN member portal, insurer apps, and registrar or AMC sites. Some older accounts still need a signed form at the branch. If the app shows “nomination registered,” download the acknowledgement.',
    },
    {
      q: 'Should minors be nominees?',
      a: 'You can usually name a minor and appoint a guardian to receive on their behalf until they turn 18. Use this when you intend the benefit for the child. Keep the guardian’s details updated.',
    },
  ],
  body: `
Adding a nominee is not one form for your whole financial life. Each product has its own record. If you complete only the savings account and skip PPF, EPF, and LIC, you have protected one slice.

Set aside an evening. Open every login you still have. Treat “I think I added someone” as not done until you see the name on screen or on a stamped acknowledgement.

![Treat nomination as a checklist across accounts—not a single form you filled years ago.](/blog/how-to-add-a-nominee-in-india/checklist.jpg)

## Bank accounts and fixed deposits

Nomination for deposits is typically registered with the branch or in net banking. Look for “Nominate” or “Manage nominee” under account services. For FDs, confirm nomination on each deposit if your bank treats them separately from the savings account.

- Name, relationship, date of birth, and address of the nominee.
- Share percentage if you name more than one person.
- A guardian if the nominee is a minor.
- Save or photograph the confirmation. Do not trust memory.

## PPF

PPF nomination is filed with the bank or post office that holds the account. Many people opened PPF a decade ago and never looked again. If you have since married or a parent has died, the old nomination may be wrong.

Ask for the nomination details on file. Update with the current form, including percentage shares if you want more than one nominee. Keep the passbook and the nomination acknowledgement together.

## EPF

EPF nomination is through the UAN member portal (Form 2 / e-nomination). Family members are typically eligible in a defined order. If you changed jobs, the UAN may still be the same—nomination does not reset just because the employer did.

- Activate UAN if it is dormant. This is the lockbox for the balance.
- Complete e-nomination and verify with Aadhaar OTP where asked.
- Tell your spouse or parent that an EPF balance exists and that a UAN is on file. You do not need to share the password.

## Insurance

Every policy has its own nominee. A LIC policy from 2009 is not updated when you add a nominee on a term plan you bought last year. Log in to each insurer, or write to the branch, and confirm the name, date of birth, and relationship.

After the 2015 changes to the Insurance Act, a spouse, parent, or child named as nominee may be a beneficial nominee. If you intend a different split among heirs, get advice and keep the policy nomination aligned with your will.

## Demat accounts and mutual funds

SEBI has pushed investors to complete nomination on demat and mutual fund folios. Use your broker or registrar (NSDL/CDSL) and each AMC or CAMS/KFintech login. A folio without nomination is a folio your family may struggle to transmit.

## After life changes

Marriage, divorce, a new child, or the death of a parent should trigger a nomination review. The form you signed at 24 is not a document for the rest of your life. Put a calendar reminder once a year: open the apps, read the names, fix what is stale.

:::callout Then record it in one place
Once each institution has the legal nomination, keep a secure list of what you own and who you named. That list is what your family will actually use. KeepMyAsset is built for that map—not as a replacement for the bank’s form.
:::

Procedures change. If a portal and a branch disagree, follow the institution’s current instruction and keep the written acknowledgement. This is a practical guide, not a substitute for the form in front of you.
`,
};

export default post;
